/**
 * Every number the dungeon is tuned with lives here: classes, monsters, items, perks, the hero
 * XP curve. Rules (how they interact) live in `engine.ts`; nothing else redeclares a value.
 */
import type { ClassId, ItemId, MonsterId, PerkId } from './types'

export interface ClassDef {
  id: ClassId
  name: string
  blurb: string
  hp: number
  atk: number
  def: number
  fov: number
  kit: ItemId[]
  /** Account level at which the class unlocks. */
  unlockLevel: number
}

export const CLASSES: Record<ClassId, ClassDef> = {
  knight: {
    id: 'knight',
    name: 'Knight',
    blurb: 'Sturdy. Bosses hurt you one less.',
    hp: 30,
    atk: 4,
    def: 2,
    fov: 6,
    kit: ['potion'],
    unlockLevel: 1,
  },
  rogue: {
    id: 'rogue',
    name: 'Rogue',
    blurb: 'Crits 20% of the time; sleeping monsters take double.',
    hp: 22,
    atk: 5,
    def: 0,
    fov: 6,
    kit: ['knife', 'knife'],
    unlockLevel: 3,
  },
  mage: {
    id: 'mage',
    name: 'Mage',
    blurb: 'Sees further; scrolls hit harder and freeze longer.',
    hp: 18,
    atk: 3,
    def: 0,
    fov: 8,
    kit: ['fireScroll', 'frostScroll'],
    unlockLevel: 6,
  },
}
export const CLASS_IDS: readonly ClassId[] = ['knight', 'rogue', 'mage']

export const ROGUE_CRIT_CHANCE = 0.2
export const MAGE_SCROLL_BONUS = 2
export const MAGE_FREEZE_BONUS = 1
export const KNIGHT_BOSS_GUARD = 1

export type Behaviour =
  | 'rush'
  | 'erratic'
  | 'split'
  | 'ranged'
  | 'counter'
  | 'blink'
  | 'ratKing'
  | 'lich'

export interface MonsterDef {
  id: MonsterId
  name: string
  hp: number
  atk: number
  def: number
  xp: number
  speed: number
  behaviour: Behaviour
  minFloor: number
  maxFloor: number
  boss?: boolean
}

export const MONSTERS: Record<MonsterId, MonsterDef> = {
  rat: {
    id: 'rat',
    name: 'rat',
    hp: 4,
    atk: 2,
    def: 0,
    xp: 2,
    speed: 100,
    behaviour: 'rush',
    minFloor: 1,
    maxFloor: 4,
  },
  bat: {
    id: 'bat',
    name: 'bat',
    hp: 5,
    atk: 2,
    def: 0,
    xp: 3,
    speed: 200,
    behaviour: 'erratic',
    minFloor: 1,
    maxFloor: 6,
  },
  slime: {
    id: 'slime',
    name: 'slime',
    hp: 8,
    atk: 2,
    def: 0,
    xp: 5,
    speed: 50,
    behaviour: 'split',
    minFloor: 2,
    maxFloor: 7,
  },
  archer: {
    id: 'archer',
    name: 'archer',
    hp: 7,
    atk: 3,
    def: 0,
    xp: 6,
    speed: 100,
    behaviour: 'ranged',
    minFloor: 3,
    maxFloor: 8,
  },
  golem: {
    id: 'golem',
    name: 'golem',
    hp: 14,
    atk: 5,
    def: 2,
    xp: 12,
    speed: 50,
    behaviour: 'counter',
    minFloor: 5,
    maxFloor: 9,
  },
  wraith: {
    id: 'wraith',
    name: 'wraith',
    hp: 9,
    atk: 4,
    def: 0,
    xp: 10,
    speed: 100,
    behaviour: 'blink',
    minFloor: 6,
    maxFloor: 10,
  },
  ratKing: {
    id: 'ratKing',
    name: 'Rat King',
    hp: 40,
    atk: 5,
    def: 1,
    xp: 40,
    speed: 100,
    behaviour: 'ratKing',
    minFloor: 5,
    maxFloor: 5,
    boss: true,
  },
  lich: {
    id: 'lich',
    name: 'Lich',
    hp: 60,
    atk: 6,
    def: 2,
    xp: 100,
    speed: 100,
    behaviour: 'lich',
    minFloor: 10,
    maxFloor: 10,
    boss: true,
  },
}
export const BOSS_OF: Record<number, MonsterId> = { 5: 'ratKing', 10: 'lich' }

/** Archer range and the Lich's longer bow. */
export const ARCHER_RANGE = 5
export const LICH_RANGE = 6
export const LICH_SHOT_DMG = 5
export const WRAITH_BLINK_EVERY = 4
export const RAT_KING_SUMMON_EVERY = 5
export const RAT_KING_MAX_RATS = 6
export const WAKE_DISTANCE = 6

export interface ItemDef {
  id: ItemId
  name: string
  blurb: string
  /** Applied on pickup (never stored). */
  permanent?: boolean
  minFloor: number
  weight: number
}

export const ITEMS: Record<ItemId, ItemDef> = {
  potion: { id: 'potion', name: 'Potion', blurb: 'Heal 10.', minFloor: 1, weight: 30 },
  bigPotion: { id: 'bigPotion', name: 'Big potion', blurb: 'Heal 20.', minFloor: 5, weight: 15 },
  knife: {
    id: 'knife',
    name: 'Throwing knife',
    blurb: '5 damage to the nearest monster in sight.',
    minFloor: 1,
    weight: 18,
  },
  fireScroll: {
    id: 'fireScroll',
    name: 'Fire scroll',
    blurb: '6 damage to everything within 3.',
    minFloor: 2,
    weight: 12,
  },
  frostScroll: {
    id: 'frostScroll',
    name: 'Frost scroll',
    blurb: 'Freeze everything in sight for 3 turns.',
    minFloor: 2,
    weight: 10,
  },
  teleportScroll: {
    id: 'teleportScroll',
    name: 'Teleport scroll',
    blurb: 'Blink somewhere far away.',
    minFloor: 3,
    weight: 8,
  },
  revealScroll: {
    id: 'revealScroll',
    name: 'Map scroll',
    blurb: 'Reveal the whole floor.',
    minFloor: 1,
    weight: 8,
  },
  sword: {
    id: 'sword',
    name: 'Sword',
    blurb: '+1 attack, forever.',
    permanent: true,
    minFloor: 2,
    weight: 6,
  },
  armor: {
    id: 'armor',
    name: 'Armor',
    blurb: '+1 defence, forever.',
    permanent: true,
    minFloor: 2,
    weight: 6,
  },
  amulet: {
    id: 'amulet',
    name: 'Amulet',
    blurb: '+5 max HP, forever.',
    permanent: true,
    minFloor: 3,
    weight: 5,
  },
}
export const POTION_HEAL = 10
export const BIG_POTION_HEAL = 20
export const KNIFE_DMG = 5
export const KNIFE_RANGE = 5
export const FIRE_DMG = 6
export const FIRE_RADIUS = 3
export const FROST_TURNS = 3
export const TELEPORT_MIN_DISTANCE = 8
export const AMULET_HP = 5

export interface PerkDef {
  id: PerkId
  name: string
  blurb: string
  /** How many times it may be taken (Infinity = stackable). */
  max: number
}

export const PERKS: Record<PerkId, PerkDef> = {
  vitality: {
    id: 'vitality',
    name: 'Vitality',
    blurb: '+8 max HP and heal 8.',
    max: Number.POSITIVE_INFINITY,
  },
  brawn: { id: 'brawn', name: 'Brawn', blurb: '+2 attack.', max: Number.POSITIVE_INFINITY },
  ironSkin: { id: 'ironSkin', name: 'Iron skin', blurb: '+1 defence.', max: 2 },
  regeneration: {
    id: 'regeneration',
    name: 'Regeneration',
    blurb: 'Heal 1 every 5 turns.',
    max: 1,
  },
  vampiric: { id: 'vampiric', name: 'Vampiric', blurb: 'Heal 1 on every kill.', max: 1 },
  keenEyes: { id: 'keenEyes', name: 'Keen eyes', blurb: 'See 2 cells further.', max: 1 },
  scavenger: {
    id: 'scavenger',
    name: 'Scavenger',
    blurb: 'Chests hold one more item; gold is worth 1.5×.',
    max: 1,
  },
  secondWind: {
    id: 'secondWind',
    name: 'Second wind',
    blurb: 'Once per run, survive a killing blow at 1 HP.',
    max: 1,
  },
  berserk: { id: 'berserk', name: 'Berserk', blurb: '+2 attack while below half health.', max: 1 },
  thorns: {
    id: 'thorns',
    name: 'Thorns',
    blurb: 'Anything that hits you in melee takes 1.',
    max: 1,
  },
}
export const PERK_IDS: readonly PerkId[] = Object.keys(PERKS) as PerkId[]
export const PERKS_OFFERED = 3
export const VITALITY_HP = 8
export const BRAWN_ATK = 2
export const REGEN_EVERY = 5
export const BERSERK_ATK = 2
export const KEEN_EYES_FOV = 2
export const SCAVENGER_GOLD = 1.5

/** Hero XP needed to *be* level `n` (cumulative): 0, 10, 30, 60, 100, 150… */
export function heroXpFor(level: number): number {
  return (10 * level * (level - 1)) / 2
}
export const LEVEL_UP_HP = 4
